/**
 * V2 Skeleton Loaders — Netflix-quality shimmer skeletons.
 * Single shimmer sweep per card, staggered wave across rows.
 */
import { cn } from '@/lib/utils';

/* ── Base shimmer block ── */
function Shimmer({ className, delay = 0 }: { className?: string; delay?: number }) {
  return (
    <div className={cn('relative overflow-hidden bg-white/[0.04] rounded-[6px]', className)}>
      <div
        className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)',
          animationDelay: `${delay}ms`,
        }}
      />
    </div>
  );
}

/* ── Book card skeleton (portrait 2:3) — matches V2BookCard sizing ── */
function BookCardSkeleton({ delay = 0, ranked }: { delay?: number; ranked?: boolean }) {
  return (
    <div
      className={cn(
        'shrink-0',
        ranked ? 'w-28 md:w-44 xl:w-56 ml-4 md:ml-8' : 'w-24 md:w-40 xl:w-52'
      )}
    >
      <Shimmer
        className="aspect-[2/3] rounded-[12px] border border-white/10"
        delay={delay}
      />
    </div>
  );
}

/* ── Book row skeleton (title + horizontal scroll) — matches V2BookRow padding/gap ── */
function BookRowSkeleton({
  delay = 0,
  ranked,
  cardCount = 6,
  titleWidth = 'w-32',
}: {
  delay?: number;
  ranked?: boolean;
  cardCount?: number;
  titleWidth?: string;
}) {
  return (
    <section className="space-y-2.5 md:space-y-3">
      {/* Title — same px / type scale as real row */}
      <div className="px-5 md:px-10 xl:px-16">
        <Shimmer
          className={cn('h-[17px] md:h-[22px] xl:h-[26px] rounded-[4px]', titleWidth)}
          delay={delay}
        />
      </div>
      {/* Scrollable card strip — same gap / padding as real row */}
      <div className="flex gap-2.5 md:gap-5 xl:gap-7 px-5 md:px-10 xl:px-16 pt-2 md:pt-6 pb-2 md:pb-8 overflow-hidden">
        {Array.from({ length: cardCount }).map((_, i) => (
          <BookCardSkeleton key={i} delay={delay + i * 80} ranked={ranked} />
        ))}
      </div>
    </section>
  );
}

/* ── Hero skeleton (full-bleed) — matches V2HeroCarousel dimensions + header chrome ── */
function HeroSkeleton() {
  return (
    <div className="relative w-full h-[85vh] overflow-hidden bg-black">
      <Shimmer className="absolute inset-0 rounded-none" />
      {/* Header chrome — BuxBox title + bell, identical positioning to V2Home */}
      <div
        className="absolute left-0 right-0 px-4 sm:px-6 z-20 flex items-center justify-between pointer-events-none"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}
      >
        <Shimmer className="h-[34px] md:h-[44px] xl:h-[52px] w-[140px] md:w-[180px] xl:w-[220px] rounded-[6px]" />
        <Shimmer className="h-10 w-10 md:h-12 md:w-12 rounded-full" delay={120} />
      </div>
      {/* Bottom gradient to match real hero */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
      {/* Hero title block — centered on mobile, left-aligned md+ (matches real hero) */}
      <div className="absolute bottom-0 left-0 right-0 px-5 md:px-12 xl:px-20 pb-6 md:pb-10">
        <div className="flex flex-col items-center md:items-start gap-2.5 md:max-w-[640px]">
          {/* Title logo placeholder */}
          <Shimmer className="h-12 md:h-16 w-56 md:w-72 rounded-[6px]" delay={200} />
          {/* BuxBox logo row */}
          <Shimmer className="h-4 w-24 rounded-[3px]" delay={280} />
          {/* Meta line */}
          <Shimmer className="h-3 w-40 rounded-[3px]" delay={340} />
          {/* CTA buttons */}
          <div className="flex items-center gap-3 mt-2">
            <Shimmer className="h-[52px] w-[140px] rounded-2xl" delay={400} />
            <Shimmer className="h-[52px] w-[52px] rounded-full" delay={460} />
          </div>
        </div>
      </div>

    </div>
  );
}

/* ── Square card skeleton (for jukebox tracks/audiobooks) ── */
function SquareCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <div className="shrink-0 w-36">
      <Shimmer className="aspect-square rounded-2xl border border-white/[0.06]" delay={delay} />
      <Shimmer className="h-3 w-24 mt-2 rounded-[3px]" delay={delay + 60} />
      <Shimmer className="h-2.5 w-16 mt-1 rounded-[3px]" delay={delay + 120} />
    </div>
  );
}

/* ── Grid card skeleton (for browse) ── */
function GridCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <div className="flex flex-col gap-2">
      <Shimmer className="aspect-[2/3] rounded-xl border border-white/[0.06]" delay={delay} />
      <Shimmer className="h-2.5 w-20 rounded-[3px]" delay={delay + 100} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   Full-page skeleton composites
   ═══════════════════════════════════════════ */

/**
 * V2 Home — hero + rows. Mirrors V2Home.tsx structure exactly:
 *   HeroSkeleton (with overlaid header chrome)
 *   → space-y-6 md:space-y-9 pt-6 md:pt-10
 *   → 8 rows (Top 10 ranked, All Stories, 3 genre rows, Coming Soon, New, Popular)
 */
export function V2HomeSkeleton() {
  return (
    <div style={{ paddingBottom: 'calc(160px + max(env(safe-area-inset-bottom), 0px))' }}>
      <HeroSkeleton />
      <div className="space-y-6 md:space-y-9 pt-6 md:pt-10 max-w-[1600px] mx-auto">
        <BookRowSkeleton delay={0}   ranked cardCount={10} titleWidth="w-[160px] md:w-[200px]" />
        <BookRowSkeleton delay={150}        cardCount={8}  titleWidth="w-[120px] md:w-[160px]" />
        <BookRowSkeleton delay={300}        cardCount={6}  titleWidth="w-[110px] md:w-[150px]" />
        <BookRowSkeleton delay={450}        cardCount={6}  titleWidth="w-[100px] md:w-[140px]" />
        <BookRowSkeleton delay={600}        cardCount={6}  titleWidth="w-[120px] md:w-[160px]" />
      </div>
    </div>
  );
}

/** V2 app gate — same surface as the first real screen, including footer chrome */
export function V2GateSkeleton() {
  return (
    <div className="dark fixed inset-0 z-50 overflow-hidden bg-background text-foreground">
      <V2HomeSkeleton />
      <div
        className="absolute bottom-0 left-0 right-0 z-50 px-4"
        style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 8px) + 10px)' }}
      >
        <div className="mx-auto flex w-fit max-w-full items-center gap-2.5">
          <Shimmer className="h-[62px] w-[254px] max-w-[calc(100vw-96px)] rounded-full border border-border/20" />
          <Shimmer className="h-[62px] w-[62px] shrink-0 rounded-full border border-border/20" delay={120} />
        </div>
      </div>
    </div>
  );
}

/** V2 Browse — search bar + tag pills + grid */
export function V2BrowseSkeleton() {
  return (
    <div className="px-4 pt-6 space-y-5">
      {/* Search bar */}
      <Shimmer className="h-10 w-full rounded-md" />
      {/* Tag pills */}
      <div className="flex gap-2 flex-wrap">
        {['w-[88px]', 'w-[80px]', 'w-[96px]', 'w-[72px]', 'w-[84px]'].map((w, i) => (
          <Shimmer key={i} className={`h-7 rounded-full ${w}`} delay={i * 60} />
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <GridCardSkeleton key={i} delay={i * 80} />
        ))}
      </div>
    </div>
  );
}

/** V2 RoxBox/Jukebox — header + featured hero + track rows */
export function V2JukeboxSkeleton() {
  return (
    <div className="min-h-screen flex flex-col" style={{ paddingBottom: 'calc(160px + max(env(safe-area-inset-bottom), 0px))' }}>
      {/* Header */}
      <div className="px-5 pb-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}>
        <Shimmer className="h-7 w-44 rounded-[4px]" />
        <Shimmer className="h-3.5 w-36 mt-1.5 rounded-[3px]" delay={100} />
      </div>
      {/* Featured 2-up */}
      <div className="px-5 pb-2 grid grid-cols-2 gap-3">
        <Shimmer className="aspect-square rounded-2xl" delay={150} />
        <Shimmer className="aspect-square rounded-2xl" delay={250} />
      </div>
      {/* Track row */}
      <div className="pt-5">
        <div className="px-5 mb-3">
          <Shimmer className="h-4 w-28 rounded-[3px]" delay={300} />
        </div>
        <div className="flex gap-3 pl-5 pr-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <SquareCardSkeleton key={i} delay={350 + i * 80} />
          ))}
        </div>
      </div>
      {/* Audiobook row */}
      <div className="pt-6">
        <div className="px-5 mb-3">
          <Shimmer className="h-4 w-24 rounded-[3px]" delay={600} />
        </div>
        <div className="flex gap-3 pl-5 pr-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <SquareCardSkeleton key={i} delay={650 + i * 80} />
          ))}
        </div>
      </div>
    </div>
  );
}
